import React, { createRef, useEffect } from 'react'

import { useNavigate } from 'react-router'
import styled, { CSS } from 'styled-components'

import { Button, CountdownTimer, FlexDeviceContainer, getPageSectionButtonColor, PageNotificationPopup, Paragraph } from '@client/components'
import { IconEnum, ResponsiveImage, Spacer } from '@client/components/atoms'
import { ResponsiveProperty, ResponsivePXValue, theme, ZeroSpace } from '@client/components/Theme'
import { PageNotificationFragment } from '@hooks/api'

interface TextButtonColors {
  textColor: string
  textHoverColor: string
}

const Container = styled.div<{ $color?: string, $isPopup?: boolean }>`
  background-color: ${(props): string => props.$color ? props.$color : props.theme.colors.oranges.coral};
  max-width: 100%;
  display: block;
  align-items: center;
  justify-content: center;
  ${ResponsivePXValue('padding', { mobile: '12px', tablet: '12px', desktop: '16px' })}

  .close-button {
    ${ZeroSpace}
  }
  .flex-container {
    align-items: center;
  }

  ${(props): CSS => {
    if (props?.$isPopup) {
      return `
        padding: 0 !important;
        border-radius: 8px;
        overflow: hidden;
        ${ResponsiveProperty('position', { mobile: 'fixed', tablet: 'absolute', desktop: 'absolute' })}
        ${ResponsivePXValue('top', { desktop: '80px', tablet: '80px' })}
        ${ResponsivePXValue('right', { desktop: '20px', tablet: '20px', mobile: '0' })}
        ${ResponsivePXValue('bottom', { mobile: '0' })}
        ${ResponsivePXValue('left', { mobile: '0' })}
      `
    }
  }}
`

const FlexSpacer = styled.div`
  flex-grow: 1;
`

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  ${ResponsiveProperty('flex-direction', { mobile: 'column', tablet: 'row', desktop: 'row' })}
  ${ResponsiveProperty('justify-content', { mobile: 'flex-start', tablet: 'center', desktop: 'center' })}

  .action-button {
    ${ResponsivePXValue('min-width', '64px')}
    ${ResponsivePXValue('margin', { mobile: '16px 0 0', tablet: '0 16px', desktop: '0 16px' })}
  }
`
const CloseButtonContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`
const NotificationWrapper = styled.div`
  display: block;
`
const IconContainer = styled.div`
  ${ResponsivePXValue('height', '24px')}
  ${ResponsivePXValue('width', '24px')}
`

export interface PageNotificationBannerProps {
  notification: PageNotificationFragment | null
  dismissLoading?: boolean
  onHeightChange?: (height: number) => void
  onLinkClicked?: () => void
  onClose?: () => void
}

export function PageNotificationBanner({ notification, dismissLoading = false, onLinkClicked, onClose, onHeightChange }: PageNotificationBannerProps): JSX.Element {

  const navigate = useNavigate()
  const mobileRef: React.RefObject<HTMLDivElement> = createRef()

  // Validate and create target date
  const targetDate = React.useMemo(() => {
    if (!notification?.endDate) return null
    const date = new Date(notification.endDate)
    return isNaN(date.getTime()) ? null : date
  }, [notification?.endDate])

  // Handle height changes with proper dependency tracking
  useEffect(() => {
    if (!onHeightChange || !mobileRef?.current) return

    const updateHeight = (): void => {
      const height = mobileRef?.current?.clientHeight ?? 0
      if (height > 0) {
        onHeightChange(height)
      }
    }

    // Initial height measurement
    updateHeight()

    // Use ResizeObserver for better performance and accuracy
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateHeight()
      })
      resizeObserver.observe(mobileRef.current)

      return () => {
        resizeObserver.disconnect()
      }
    }

    // Fallback to window resize for older browsers
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateHeight)
    }
  }, [onHeightChange, notification])

  const _handleOnLinkPressed = (): void => {
    if (!notification?.link) return

    try {
      // Validate and sanitize URL
      const link = notification.link.trim()
      if (!link) return

      // Check if link is external (starts with http:// or https://)
      const isExternal = link.startsWith('http://') || link.startsWith('https://')

      if (isExternal) {
        // Validate URL format for security
        try {
          const url = new URL(link)
          // Only allow http and https protocols
          if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            console.warn('Invalid URL protocol:', link)
            return
          }
          // Open external link in new tab
          const newWindow = window.open(url.toString(), '_blank', 'noopener,noreferrer')
          if (!newWindow) {
            console.warn('Popup blocked or failed to open:', link)
          }
        } catch (urlError) {
          console.warn('Invalid URL format:', link, urlError)
          return
        }
      } else {
        // Internal link - validate it starts with /
        if (!link.startsWith('/')) {
          console.warn('Invalid internal link format:', link)
          return
        }
        // Internal link - use navigate
        navigate(link)
      }

      onLinkClicked?.()
    } catch (error) {
      console.error('Error handling link click:', error)
    }
  }

  const buttonColors: TextButtonColors = {
    textColor: notification?.textColor || theme.colors.whites.pureWhite,
    textHoverColor: theme.colors.whites.desertStorm,
  }

  // Create custom button colors if ctaBackground and/or ctaText are provided
  const customButtonColors = notification?.ctaBackground || notification?.ctaText
    ? {
        backgroundColor: notification?.ctaBackground || undefined,
        color: notification?.ctaText || undefined,
        borderColor: notification?.ctaBackground || undefined,
      }
    : undefined

  // Early return if no notification
  if (!notification) {
    return <></>
  }

  return (
    <Container $isPopup={notification?.isPopup} $color={notification?.backgroundColor}>
      <Choose>
        <When condition={!!notification?.isPopup}>
          <PageNotificationPopup
            notification={notification}
            dismissLoading={dismissLoading}
            onLinkClicked={onLinkClicked}
            onClose={onClose}
          />
        </When>
        <Otherwise>
          <FlexDeviceContainer ref={mobileRef} $mobile $tablet>
            <NotificationWrapper>
              <Choose>
                <When condition={!!notification?.link}>
                  <ContentContainer onClick={_handleOnLinkPressed}>
                    <Paragraph variant='p1' align='center' color={buttonColors.textColor}>{notification?.text}</Paragraph>
                    <If condition={notification?.shouldCountDownDisplay}>
                      <Spacer universal='16px' variant='horizontal' />
                      <CountdownTimer targetDate={targetDate} />
                    </If>
                  </ContentContainer>
                </When>
                <Otherwise>
                  <ContentContainer>
                    <Paragraph variant='p1' align='center' color={buttonColors.textColor}>{notification?.text}</Paragraph>
                    <If condition={notification?.shouldCountDownDisplay}>
                      <Spacer universal='16px' variant='horizontal' />
                      <CountdownTimer targetDate={targetDate} />
                    </If>
                  </ContentContainer>
                </Otherwise>
              </Choose>
              <CloseButtonContainer>
                <Button
                  color='transparent'
                  size='small'
                  icon={IconEnum.CLOSE_OUTLINE}
                  iconColor={buttonColors.textColor}
                  loading={dismissLoading}
                  disabled={dismissLoading}
                  onClick={() => onClose?.()} />
              </CloseButtonContainer>
            </NotificationWrapper>
          </FlexDeviceContainer>
          <FlexDeviceContainer className='flex-container' $desktop>
            <FlexSpacer />
            <If condition={!!notification?.icon}>
              <IconContainer>
                <ResponsiveImage image={notification.icon} />
              </IconContainer>
              <Spacer universal='8px' variant='horizontal' />
            </If>
            <ContentContainer>
              <Paragraph variant='p1' className='body-text' color={buttonColors.textColor}>{notification?.text}</Paragraph>
              <If condition={!!notification?.link}>
                <Button
                  color={notification?.ctaBackground ? getPageSectionButtonColor(notification.ctaBackground) : 'black'}
                  title={notification?.linkText}
                  disabled={dismissLoading}
                  onClick={(e) => {
                    e.preventDefault()
                    _handleOnLinkPressed()
                  }}
                  href={notification?.link}
                  className='action-button'
                  customButtonColors={customButtonColors} />
              </If>
              <Spacer universal='16px' variant='horizontal' />
              <If condition={notification?.shouldCountDownDisplay}>
                <CountdownTimer targetDate={targetDate} />
              </If>
            </ContentContainer>
            <FlexSpacer />
            <If condition={notification?.canClose}>
              <Spacer universal='8px' />
              <Button
                color='transparent'
                size='small'
                icon={IconEnum.CLOSE_OUTLINE}
                iconColor={buttonColors.textColor}
                loading={dismissLoading}
                disabled={dismissLoading}
                onClick={() => onClose?.()} />
            </If>
          </FlexDeviceContainer>
        </Otherwise>
      </Choose>
    </Container>
  )
}
// on mobile Must be dismissible ! but x in top center
